import apiClient from '../helpers/apiClient'

/**
 * Shape estricto de las preferencias de notificacion (PR-I-63 backend).
 *
 * IMPORTANTE (seguridad): este tipo es la unica fuente de verdad para el
 * payload que enviamos al backend. NO incluimos `user` ni cualquier otro
 * campo — el backend lo derive de la sesion autenticada. Enviarlo abriria
 * un vector de parameter-tampering / IDOR (el usuario podria intentar
 * mutar preferencias ajenas).
 */
export interface NotificationSettings {
  email_new_introduction_requests: boolean
  email_new_forum_messages: boolean
  email_experience_updates: boolean
  email_new_member_invitations: boolean
  marketing_product_updates: boolean
  forum_mentions_and_replies: boolean
}

/**
 * Patch parcial: cualquier subconjunto de las 6 keys. NO permitimos claves
 * ajenas (allowlist implicita gracias a `Partial<NotificationSettings>`),
 * y en el service filtramos por seguridad defensiva.
 */
export type NotificationSettingsPatch = Partial<NotificationSettings>

const ALLOWED_KEYS: ReadonlyArray<keyof NotificationSettings> = [
  'email_new_introduction_requests',
  'email_new_forum_messages',
  'email_experience_updates',
  'email_new_member_invitations',
  'marketing_product_updates',
  'forum_mentions_and_replies',
]

/**
 * Sanea un patch entrante: descarta cualquier clave fuera de la allowlist
 * y coacciona a boolean estricto (evita que un string "false" o un number
 * llegue al backend y provoque interpretaciones raras).
 */
const sanitizePatch = (patch: NotificationSettingsPatch): NotificationSettingsPatch => {
  const clean: NotificationSettingsPatch = {}
  for (const key of ALLOWED_KEYS) {
    if (Object.prototype.hasOwnProperty.call(patch, key)) {
      const raw = patch[key]
      if (typeof raw === 'boolean') {
        clean[key] = raw
      }
    }
  }
  return clean
}

/**
 * Backend serializa camelCase globalmente (djangorestframework-camel-case).
 * Este mapper convierte la respuesta a snake_case, que es el shape estable
 * que el resto del front consume. Solo lee las 6 claves esperadas + updatedAt.
 */
type CamelPayload = Partial<Record<string, unknown>>

const fromCamel = (raw: CamelPayload): NotificationSettings => ({
  email_new_introduction_requests: Boolean(raw.emailNewIntroductionRequests),
  email_new_forum_messages: Boolean(raw.emailNewForumMessages),
  email_experience_updates: Boolean(raw.emailExperienceUpdates),
  email_new_member_invitations: Boolean(raw.emailNewMemberInvitations),
  marketing_product_updates: Boolean(raw.marketingProductUpdates),
  forum_mentions_and_replies: Boolean(raw.forumMentionsAndReplies),
})

/**
 * GET /notifications/settings/
 * Devuelve las 6 preferencias booleanas del usuario autenticado.
 */
export async function getNotificationSettings(): Promise<NotificationSettings> {
  const { data } = await apiClient.get<CamelPayload>('/notifications/settings/')
  return fromCamel(data)
}

/**
 * PATCH /notifications/settings/
 *
 * Envia SOLO las claves de la allowlist coaccionadas a boolean estricto.
 * Ignoramos silenciosamente cualquier clave extra (defensive layer aunque
 * el backend tambien la ignore).
 */
export async function updateNotificationSettings(
  patch: NotificationSettingsPatch
): Promise<NotificationSettings> {
  const payload = sanitizePatch(patch)
  // apiClient tiene interceptor camel-case en la request (convierte snake_case
  // del payload a camelCase antes del envio). La respuesta viene camelCase y
  // la desnormalizamos localmente para mantener el shape consistente en el UI.
  const { data } = await apiClient.patch<CamelPayload>(
    '/notifications/settings/',
    payload
  )
  return fromCamel(data)
}
